import { proposalFontFamilies } from './proposalStylesUtils';

/** Échelle type Mantine / navigateur — tailles en rem (racine = 1rem). */
export const DEFAULT_RICH_TEXT_FONT_SIZES_REM = [
  0.625, 0.75, 0.875, 1, 1.125, 1.25, 1.5, 1.75, 2, 2.25, 3,
];

/** @deprecated Préférer {@link DEFAULT_RICH_TEXT_FONT_SIZES_REM} */
export const DEFAULT_RICH_TEXT_FONT_SIZES = DEFAULT_RICH_TEXT_FONT_SIZES_REM.map(
  (rem) => Math.round(rem * 16),
);

export const FONT_SIZE_REM_MIN = 0.5;
export const FONT_SIZE_REM_MAX = 6;
export const FONT_SIZE_REM_DEFAULT = 1;
export const FONT_SIZE_REM_STEP = 0.0625;

/**
 * Taille racine du document (px) — base des conversions rem ↔ px.
 * @param {Document} [doc]
 */
export function getRootFontSizePx(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc?.documentElement) return 16;
  const px = parseFloat(window.getComputedStyle(doc.documentElement).fontSize);
  return Number.isFinite(px) && px > 0 ? px : 16;
}

/**
 * Parse une font-size CSS → rem (px/em/rem supportés).
 * @param {string|number|null|undefined} value
 * @param {number} [rootPx]
 * @returns {number|null}
 */
export function parseFontSizeRem(value, rootPx = getRootFontSizePx()) {
  if (value == null || value === '') return null;
  const str = String(value).trim().toLowerCase();
  if (str.endsWith('rem')) {
    const n = parseFloat(str);
    return Number.isFinite(n) ? n : null;
  }
  if (str.endsWith('em')) {
    const n = parseFloat(str);
    return Number.isFinite(n) ? n : null;
  }
  if (str.endsWith('px')) {
    const px = parseFloat(str);
    return Number.isFinite(px) ? px / rootPx : null;
  }
  const n = parseFloat(str);
  if (!Number.isFinite(n)) return null;
  // Legacy : grand entier sans unité = px (ex. import proposal)
  if (Number.isInteger(n) && n > 4) return n / rootPx;
  return n;
}

/** @deprecated Préférer {@link parseFontSizeRem} */
export function parseFontSizePx(value, rootPx = getRootFontSizePx()) {
  const rem = parseFontSizeRem(value, rootPx);
  return rem == null ? null : rem * rootPx;
}

/**
 * @param {number} rem
 * @returns {string}
 */
export function formatFontSizeRem(rem) {
  if (rem == null || !Number.isFinite(Number(rem))) return '';
  const clamped = Math.max(FONT_SIZE_REM_MIN, Math.min(FONT_SIZE_REM_MAX, Number(rem)));
  const rounded = Math.round(clamped * 1000) / 1000;
  return `${rounded}rem`;
}

/**
 * Taille voisine, d'un pas (1 px) vers le haut ou le bas, bornée. Une sélection
 * sans taille part de la taille par défaut, comme le classique.
 * @param {number | null | undefined} rem
 * @param {1 | -1} direction
 */
export function stepFontSizeRem(rem, direction) {
  const current = Number.isFinite(rem) ? rem : FONT_SIZE_REM_DEFAULT;
  // `formatFontSizeRem` arrondit à 3 décimales : une taille déjà posée peut
  // valoir 17,008 px. Repartir du pixel entier évite que l'écart s'accumule.
  const snapped = Math.round(current / FONT_SIZE_REM_STEP) * FONT_SIZE_REM_STEP;
  const next = snapped + direction * FONT_SIZE_REM_STEP;
  return Math.max(FONT_SIZE_REM_MIN, Math.min(FONT_SIZE_REM_MAX, next));
}

/** @deprecated Préférer {@link formatFontSizeRem} */
export function formatFontSizePx(px, rootPx = getRootFontSizePx()) {
  if (px == null || !Number.isFinite(Number(px))) return '';
  return formatFontSizeRem(Number(px) / rootPx);
}

/**
 * @param {object} [proposalStyles]
 * @returns {{ label: string, value: string }[]}
 */
export function buildRichTextFontFamilyOptions(proposalStyles) {
  const names = new Set();

  const add = (name) => {
    if (!name) return;
    const primary = String(name).split(',')[0].replace(/["']/g, '').trim();
    if (primary) names.add(primary);
  };

  proposalFontFamilies(proposalStyles).forEach(add);

  return Array.from(names)
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((value) => ({ value, label: value }));
}

/**
 * @param {number[]} [sizesRem]
 * @returns {{ label: string, value: string }[]}
 */
export function buildRichTextFontSizeOptions(sizesRem = DEFAULT_RICH_TEXT_FONT_SIZES_REM) {
  return sizesRem.map((rem) => {
    const css = formatFontSizeRem(rem);
    return { value: css, label: css };
  });
}

const primaryFontName = (name) => (
  name ? String(name).split(',')[0].replace(/["']/g, '').trim() : ''
);

/** Police posée sur le texte sélectionné (mark), `null` s'il hérite. */
export function getSelectionOwnFontFamily(editor) {
  if (!editor || editor.isDestroyed) return null;
  return primaryFontName(editor.getAttributes('textStyle')?.fontFamily) || null;
}

/**
 * Police qu'aurait le texte sélectionné sans police posée : celle de son
 * paragraphe ou titre, héritée du thème (police des titres pour un titre).
 * @returns {string|null}
 */
export function getSelectionDefaultFontFamily(editor) {
  if (!editor || editor.isDestroyed || !editor.view) return null;
  const { view } = editor;
  try {
    const { $from } = editor.state.selection;
    const block = $from.depth > 0 ? view.nodeDOM($from.before($from.depth)) : null;
    // Pas d'`instanceof HTMLElement` : le canvas est une iframe, ses nœuds
    // viennent d'une autre fenêtre.
    const el = block?.nodeType === Node.ELEMENT_NODE ? block : view.dom;
    const win = el.ownerDocument?.defaultView || window;
    return primaryFontName(win.getComputedStyle(el).fontFamily) || null;
  } catch {
    return null;
  }
}
